// frontend/src/components/AuthRequiredModal.js
// W3 — In-context login modal for account-required Looks actions (follow,
// save, repost, collections). Instead of kicking the visitor to /login, this
// lets them authenticate right where they are (email + password, then the
// 6-digit OTP from their inbox — mirroring the existing /api/auth/login ->
// /api/auth/verify-code flow). On success it persists the session exactly like
// VerifyCodePage and fires onAuthed() so the original action completes.
//
// Props:
//   open          — bool
//   onClose       — fn
//   intent        — string, e.g. "Save this look" / "Follow this professional"
//   redirectPath  — string (full-page login/signup return path)
//   onAuthed      — optional fn, called after successful login

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaTimes, FaEnvelope, FaLock, FaSpinner, FaRegBookmark } from "react-icons/fa";

const DEFAULT_API = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

function AuthRequiredModal({
  open,
  onClose,
  intent = "do that",
  redirectPath,
  onAuthed,
}) {
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const emailRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep("credentials");
      setError("");
      setCode("");
      setPassword("");
      setResendIn(0);
      setTimeout(() => emailRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  if (!open) return null;

  const returnPath = redirectPath || `${window.location.pathname}${window.location.search}`;

  const persistSession = (payload) => {
    const { token, expiresAt, user } = payload;
    localStorage.setItem("ogestyle_token", token);
    if (expiresAt) localStorage.setItem("ogestyle_token_expires", expiresAt);
    if (user?.role) localStorage.setItem("ogestyle_role", user.role);
    if (Array.isArray(user?.roles)) localStorage.setItem("ogestyle_roles", JSON.stringify(user.roles));
    if (user?.hasCompletedSetup !== undefined) {
      localStorage.setItem("ogestyle_hasCompletedSetup", user.hasCompletedSetup !== false ? "true" : "false");
    }
    window.dispatchEvent(new Event("authStateChanged"));
  };

  const handleCredentials = async (e) => {
    e?.preventDefault?.();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${DEFAULT_API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password, rememberMe: false }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) setError("Incorrect email or password.");
        else if (data?.unverified) setError("Please verify your email first — check your inbox for the verification link.");
        else if (data?.incompleteRegistration) setError("Please finish your registration using the email link that was sent to you.");
        else setError(data?.message || "Unable to log in. Please try again.");
        return;
      }
      if (data?.requiresOtp) {
        setStep("otp");
        setResendIn(60);
        setError("");
      } else {
        setError("Unexpected login response. Please try the full login page.");
      }
    } catch (err) {
      setError("Unable to reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${DEFAULT_API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password, resendCode: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.message || "Unable to resend the code.");
        return;
      }
      setResendIn(60);
      setError("Code resent — check your inbox.");
    } catch (err) {
      setError("Unable to resend the code.");
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedEmail || !trimmedCode) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${DEFAULT_API}/api/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, code: trimmedCode }),
      });
      const data = await res.json();
      if (!res.ok || data?.status !== "success" || !data?.token) {
        setError(data?.message || "Invalid or expired code. Please try again.");
        return;
      }
      persistSession(data);
      onClose();
      if (typeof onAuthed === "function") onAuthed();
    } catch (err) {
      setError("Unable to verify the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    padding: "11px 12px",
    fontSize: "14px",
    outline: "none",
  };

  const primaryBtnStyle = {
    width: "100%",
    border: "none",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
    color: "#fff",
    padding: "13px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: busy ? "default" : "pointer",
    opacity: busy ? 0.65 : 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    boxShadow: "0 6px 16px rgba(37, 99, 235, 0.28)",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2,6,23,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 4000,
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Log in"
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "#fff",
          borderRadius: "18px",
          boxShadow: "0 24px 60px rgba(2,6,23,0.45)",
          padding: "26px 24px 22px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            zIndex: 5,
            width: "32px",
            height: "32px",
            borderRadius: "999px",
            border: "1px solid rgba(255,255,255,0.3)",
            background: "rgba(255,255,255,0.16)",
            color: "#fff",
            fontSize: "16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FaTimes />
        </button>

        {/* Blue gradient header — matches the /login branding panel */}
        <div
          style={{
            margin: "-26px -24px 20px",
            padding: "26px 24px 22px",
            background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              margin: "0 auto 12px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "19px",
            }}
          >
            <FaRegBookmark />
          </div>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#fff" }}>
            {step === "otp" ? "Check your email" : "Log in to continue"}
          </h2>
          <p style={{ margin: "6px 0 0", fontSize: "13px", color: "rgba(255,255,255,0.92)", lineHeight: 1.5 }}>
            {step === "otp" ? (
              <>
                We sent a 6-digit code to <strong style={{ color: "#fff" }}>{email.trim()}</strong>. Enter it
                below to {intent.toLowerCase()}.
              </>
            ) : (
              <>
                A free account lets you {intent.toLowerCase()}. Enter your details — it takes
                under a minute.
              </>
            )}
          </p>
        </div>

        {error && (
          <div
            style={{
              marginBottom: "12px",
              padding: "9px 11px",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              background: "#fef2f2",
              color: "#991b1b",
              fontSize: "12.5px",
              lineHeight: 1.4,
            }}
          >
            {error}
          </div>
        )}

        {step === "credentials" ? (
          <form onSubmit={handleCredentials} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <div style={{ position: "relative" }}>
              <FaEnvelope size={13} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
              <input
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                autoComplete="email"
                style={{ ...inputStyle, paddingLeft: "34px" }}
              />
            </div>
            <div style={{ position: "relative" }}>
              <FaLock size={13} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                style={{ ...inputStyle, paddingLeft: "34px" }}
              />
            </div>
            <button type="submit" disabled={busy} style={primaryBtnStyle}>
              {busy ? <FaSpinner className="fa-spin" style={{ animation: "spin 1s linear infinite" }} /> : "Continue"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <input
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              style={{ ...inputStyle, textAlign: "center", fontSize: "18px", letterSpacing: "6px", fontWeight: 700 }}
              autoFocus
            />
            <button type="submit" disabled={busy} style={primaryBtnStyle}>
              {busy ? <FaSpinner className="fa-spin" style={{ animation: "spin 1s linear infinite" }} /> : "Verify & continue"}
            </button>
            <div style={{ textAlign: "center" }}>
              <button
                type="button"
                onClick={handleResend}
                disabled={busy || resendIn > 0}
                style={{
                  border: "none",
                  background: "transparent",
                  color: resendIn > 0 ? "#9ca3af" : "#2563eb",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  cursor: resendIn > 0 ? "default" : "pointer",
                }}
              >
                {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
              </button>
            </div>
          </form>
        )}

        {/* Footer links */}
        <div style={{ marginTop: "16px", paddingTop: "14px", borderTop: "1px solid #edf1f6", textAlign: "center", fontSize: "12.5px" }}>
          {step === "credentials" ? (
            <>
              <Link
                to={`/signup?redirect=${encodeURIComponent(returnPath)}`}
                style={{ color: "#1d4f80", fontWeight: 700, textDecoration: "none" }}
              >
                New here? Create a free account
              </Link>
              <span style={{ color: "#d1d5db", margin: "0 8px" }}>·</span>
              <Link
                to={`/login?redirect=${encodeURIComponent(returnPath)}`}
                style={{ color: "#2563eb", textDecoration: "none" }}
              >
                Full login page
              </Link>
            </>
          ) : (
            <button
              onClick={() => {
                setStep("credentials");
                setError("");
              }}
              style={{ border: "none", background: "transparent", color: "#2563eb", fontSize: "12.5px", fontWeight: 600, cursor: "pointer" }}
            >
              ← Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthRequiredModal;
